import React, { useState } from "react";
import { Container, Form, Button, Table, Alert, Spinner } from "react-bootstrap";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import "../styles/InvoiceUpload.css";

import axios from "../utils/api";

const API_URL = process.env.REACT_APP_API_URL_BASE || "http://localhost:6020/";
const INVOICE_API_URL =
  process.env.REACT_APP_INVOICE_PARSER_URL || `${API_URL}api/invoice/parse`;
const INVOICE_XLSX_URL = `${API_URL}api/invoice/parse-xlsx`;

function InvoiceUpload() {
  const [file, setFile] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  // For xlsx multi-invoice mode
  const [multiInvoices, setMultiInvoices] = useState(null); // array of invoices
  const [activeInvoiceIdx, setActiveInvoiceIdx] = useState(0);
  const [savedInvoiceIds, setSavedInvoiceIds] = useState({}); // invoiceNumber → id
  const [rawResponse, setRawResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isXlsx = (f) => {
    if (!f) return false;
    const ext = f.name.split(".").pop().toLowerCase();
    return ext === "xlsx" || ext === "xls";
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validTypes = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/csv",
        "application/pdf",
      ];
      const ext = selectedFile.name.split(".").pop().toLowerCase();
      const validExts = ["xlsx", "xls", "csv", "pdf"];

      if (!validTypes.includes(selectedFile.type) && !validExts.includes(ext)) {
        setError("Please upload a valid file (Excel, CSV, or PDF)");
        setFile(null);
        return;
      }

      setFile(selectedFile);
      setError(null);
      setMultiInvoices(null);
      setInvoiceData(null);
      setSavedInvoiceIds({});
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError("Please select a file first");
      return;
    }

    setLoading(true);
    setError(null);
    setMultiInvoices(null);
    setInvoiceData(null);
    setSavedInvoiceIds({});

    try {
      // ── Excel → backend parse-xlsx ───────────────────────────
      if (isXlsx(file)) {
        const formData = new FormData();
        formData.append("file", file);

        const response = await axios.post(INVOICE_XLSX_URL, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (!response.data.success) {
          throw new Error(response.data.message || "Failed to parse Excel file");
        }

        const invoices = response.data.invoices || [];
        if (invoices.length === 0) throw new Error("No invoices found in file");

        // Merge all invoices into one with an arbitrary name derived from the filename
        const allItems = invoices.flatMap((inv) => inv.items);
        const totalAmount = invoices.reduce((s, inv) => s + (inv.invoice_amount || 0), 0);
        const baseName = file.name.replace(/\.[^.]+$/, "").replace(/[^a-zA-Z0-9]/g, "-");
        const arbitraryInvoiceNumber = baseName;

        setInvoiceData({
          metadata: {
            fileName: file.name,
            uploadDate: new Date().toLocaleDateString(),
            totalItems: allItems.length,
            totalAmount,
            invoiceNumber: arbitraryInvoiceNumber,
            vendorName: invoices[0]?.vendor_name || "Pitco Foods",
            invoiceDate: invoices[0]?.invoice_date || "",
            tempFile: "",
          },
          lineItems: allItems,
        });
        setRawResponse(response.data);
        toast.success(`Parsed ${invoices.length} invoice(s) → merged into one (${allItems.length} items)`);
        return;
      }

      // ── PDF → backend parse ──────────────────────────────────
      if (file.type === "application/pdf") {
        const formData = new FormData();
        formData.append("file", file);

        const response = await axios.post(INVOICE_API_URL, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (!response.data.success) {
          throw new Error(response.data.message || "Failed to parse PDF");
        }

        const lineItems = response.data.items || response.data.lineItems || [];
        const totalItems = response.data.count ?? lineItems.length;
        const totalAmount = lineItems.reduce((sum, item) => {
          return sum + (parseFloat(item.total ?? item.subtotal ?? 0) || 0);
        }, 0);

        setInvoiceData({
          metadata: {
            fileName: file.name,
            uploadDate: new Date().toLocaleDateString(),
            totalItems,
            totalAmount,
            invoiceNumber: response.data.invoice_number || "",
            vendorName: response.data.vendor_name || "",
            invoiceDate: response.data.invoice_date || "",
            tempFile: response.data.tempFile || "",
          },
          lineItems,
        });
        setRawResponse(response.data);
        toast.success("Invoice parsed successfully!");
        return;
      }

      // ── CSV / other → client-side parse ─────────────────────
      {
        const parsedData = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            try {
              const wb = XLSX.read(e.target.result, { type: "binary" });
              const ws = wb.Sheets[wb.SheetNames[0]];
              resolve(XLSX.utils.sheet_to_json(ws));
            } catch (err) {
              reject(new Error("Failed to parse file: " + err.message));
            }
          };
          reader.onerror = () => reject(new Error("Failed to read file"));
          reader.readAsBinaryString(file);
        });

        if (!parsedData || parsedData.length === 0)
          throw new Error("No data found in the file");

        const invoiceMetadata = {
          fileName: file.name,
          uploadDate: new Date().toLocaleDateString(),
          totalItems: parsedData.length,
          totalAmount: parsedData.reduce((sum, item) => {
            const amount = parseFloat(
              item.Amount ||
              item.amount ||
              item.Total ||
              item.total ||
              item.TOTAL ||
              0
            );
            return sum + (isNaN(amount) ? 0 : amount);
          }, 0),
          invoiceNumber: "",
          vendorName: "",
          invoiceDate: "",
          tempFile: "",
        };

        setInvoiceData({
          metadata: invoiceMetadata,
          lineItems: parsedData,
        });

        toast.success("File parsed. Note: vendor/invoice number not detected for CSV files.");
      }
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.message || "Error processing file");
      setRawResponse(null);
      toast.error("Error: " + (err.message || "Failed to process invoice"));
    } finally {
      setLoading(false);
    }
  };

  // ── Helpers ───────────────────────────────────────────────────

  const getTableColumns = (items) => {
    if (!items || items.length === 0) return [];
    return Object.keys(items[0]).filter((k) => k !== "raw_lines");
  };

  const [expandedRawLines, setExpandedRawLines] = useState(() => new Set());
  const toggleRawLines = (key) => {
    setExpandedRawLines((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleClear = () => {
    setFile(null);
    setInvoiceData(null);
    setMultiInvoices(null);
    setActiveInvoiceIdx(0);
    setSavedInvoiceIds({});
    setError(null);
    setRawResponse(null);
  };

  // ── Save a single invoice object (from multi or single mode) ─
  const saveOneInvoice = async (inv, fileName) => {
    const payload = {
      vendor_name: inv.vendor_name,
      invoice_number: inv.invoice_number,
      invoice_date: inv.invoice_date,
      fileName: fileName || file?.name || "",
      uploadDate: new Date().toLocaleDateString(),
      totalItems: inv.count,
      totalAmount: inv.invoice_amount,
      items: inv.items,
    };
    const response = await axios.post(`${API_URL}api/invoice`, payload);
    if (!response.data?.success) {
      throw new Error(response.data?.message || "Failed to save invoice");
    }
    return response.data;
  };

  // Save current active invoice (multi-invoice xlsx mode)
  const handleSaveCurrentInvoice = async () => {
    if (!multiInvoices) return;
    const inv = multiInvoices[activeInvoiceIdx];
    setLoading(true);
    setError(null);
    try {
      const result = await saveOneInvoice(inv, file?.name);
      setSavedInvoiceIds((prev) => ({
        ...prev,
        [inv.invoice_number]: result.invoiceId,
      }));
      toast.success(`Saved invoice #${inv.invoice_number}`);
    } catch (err) {
      const status = err?.response?.status;
      const apiMsg = err?.response?.data?.message;
      if (status === 409) {
        toast.error(apiMsg || "Invoice already exists");
        setError(apiMsg || "Invoice already exists");
      } else {
        const msg = apiMsg || err.message || "Failed to save";
        toast.error(msg);
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  // Save all invoices in the xlsx batch
  const handleSaveAllInvoices = async () => {
    if (!multiInvoices) return;
    setLoading(true);
    setError(null);
    let saved = 0, skipped = 0;
    const newIds = { ...savedInvoiceIds };
    for (const inv of multiInvoices) {
      if (newIds[inv.invoice_number]) { skipped++; continue; }
      try {
        const result = await saveOneInvoice(inv, file?.name);
        newIds[inv.invoice_number] = result.invoiceId;
        saved++;
      } catch (err) {
        const status = err?.response?.status;
        if (status === 409) { skipped++; }
        else { console.error(`Failed to save ${inv.invoice_number}:`, err.message); }
      }
    }
    setSavedInvoiceIds(newIds);
    setLoading(false);
    toast.success(`Saved ${saved} invoice(s)${skipped ? `, ${skipped} skipped (already exist)` : ""}`);
  };

  // Save single PDF invoice
  const handleSaveInvoice = async () => {
    if (!invoiceData) {
      setError("Nothing to save yet. Upload and parse an invoice first.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        vendor_name: invoiceData.metadata.vendorName,
        invoice_number: invoiceData.metadata.invoiceNumber,
        invoice_date: invoiceData.metadata.invoiceDate,
        fileName: invoiceData.metadata.fileName,
        uploadDate: invoiceData.metadata.uploadDate,
        totalItems: invoiceData.metadata.totalItems,
        totalAmount: invoiceData.metadata.totalAmount,
        items: invoiceData.lineItems,
        tempFile: rawResponse?.tempFile,
      };

      const saveUrl = `${API_URL}api/invoice`;
      const response = await axios.post(saveUrl, payload);

      if (!response.data?.success) {
        throw new Error(response.data?.message || "Failed to save invoice");
      }

      toast.success(`Saved invoice (${response.data.invoiceId})`);
    } catch (err) {
      console.error("Save error:", err);

      const status = err?.response?.status;
      const apiMessage = err?.response?.data?.message;
      const existingId = err?.response?.data?.invoiceId;
      if (status === 409) {
        const msg = apiMessage || "Invoice already exists (not overwritten)";
        toast.error(existingId ? `${msg}. Existing id: ${existingId}` : msg);
        setError(msg);
        return;
      }

      setError(err.message || apiMessage || "Error saving invoice");
      toast.error("Error: " + (err.message || apiMessage || "Failed to save invoice"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="invoice-upload-container mt-4">
      <h2 className="mb-4">Invoice Uploader</h2>

      {/* Upload Section */}
      <div className="upload-section card p-4 mb-4">
        <h5 className="mb-3">Upload Invoice</h5>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Select Invoice File (Excel, CSV, or PDF)</Form.Label>
            <Form.Control
              type="file"
              onChange={handleFileChange}
              accept=".xlsx,.xls,.csv,.pdf"
              disabled={loading}
            />
          </Form.Group>

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <div className="button-group">
            <Button
              variant="primary"
              onClick={handleUpload}
              disabled={!file || loading}
              className="me-2"
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Processing...
                </>
              ) : (
                "Upload & Parse"
              )}
            </Button>

            <Button
              variant="success"
              onClick={handleSaveInvoice}
              disabled={!invoiceData || loading}
              className="me-2"
            >
              Save Invoice
            </Button>

            <Button
              variant="outline-danger"
              onClick={handleClear}
              disabled={!file && !invoiceData}
            >
              Clear
            </Button>
          </div>
        </Form>
      </div>

      {/* Invoice Metadata */}
      {invoiceData && (
        <div className="metadata-section card p-4 mb-4">
          <h5 className="mb-3">Invoice Summary</h5>
          <div className="row">
            <div className="col-md-3">
              <p>
                <strong>Invoice #:</strong> {invoiceData.metadata.invoiceNumber || "—"}
              </p>
            </div>
            <div className="col-md-2">
              <p>
                <strong>Invoice Date:</strong> {invoiceData.metadata.invoiceDate || "—"}
              </p>
            </div>
            <div className="col-md-5">
              <p>
                <strong>Vendor:</strong> {invoiceData.metadata.vendorName || "—"}
              </p>
            </div>
            <div className="col-md-2">
              <p>
                <strong>File:</strong> {invoiceData.metadata.fileName}
              </p>
            </div>
            <div className="col-md-2">
              <p>
                <strong>Upload Date:</strong> {invoiceData.metadata.uploadDate}
              </p>
            </div>
          </div>
          <div className="row">
            <div className="col-md-3">
              <p>
                <strong>Total Items:</strong> {invoiceData.metadata.totalItems}
              </p>
            </div>
            <div className="col-md-3">
              <p>
                <strong>Total Amount:</strong> ${(invoiceData.metadata.totalAmount || 0).toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Line Items Table */}
      {invoiceData && invoiceData.lineItems && invoiceData.lineItems.length > 0 && (
        <div className="table-section card p-4">
          <h5 className="mb-3">Invoice Line Items</h5>
          <div className="table-responsive">
            <Table striped bordered hover>
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  {getTableColumns(invoiceData.lineItems).map((col) => (
                    <th key={col}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoiceData.lineItems.map((item, index) => {
                  const rowKey = `upload-${index}`;
                  return (
                  <React.Fragment key={index}>
                  <tr>
                    <td>
                      {item.raw_lines?.length ? (
                        <span role="button" style={{cursor:"pointer",userSelect:"none"}} onClick={() => toggleRawLines(rowKey)}>
                          {expandedRawLines.has(rowKey) ? "−" : "+"}
                        </span>
                      ) : null}{" "}{index + 1}
                    </td>
                    {getTableColumns(invoiceData.lineItems).map((col) => {
                      const value = item[col];
                      let displayValue = value;

                      // Format numeric columns
                      if (typeof value === "string" || typeof value === "number") {
                        const lowerCol = col.toLowerCase();
                        // Check if this is a price/currency column
                        if (lowerCol.includes("price") || 
                            lowerCol.includes("subtotal") || 
                            lowerCol.includes("total") ||
                            lowerCol.includes("disc") ||
                            lowerCol.includes("adj")) {
                          displayValue = `$${parseFloat(value).toFixed(2)}`;
                        }
                        // Check if this is a numeric column (qty, ppc, etc)
                        else if (lowerCol.includes("qty") || 
                                 lowerCol.includes("ppc") ||
                                 lowerCol.includes("quantity")) {
                          displayValue = parseFloat(value).toFixed(2);
                        }
                      }

                      return (
                        <td key={`${index}-${col}`} className="text-nowrap">
                          {displayValue}
                        </td>
                      );
                    })}
                  </tr>
                  {expandedRawLines.has(rowKey) && item.raw_lines?.length ? (
                    <tr>
                      <td colSpan={getTableColumns(invoiceData.lineItems).length + 1} style={{ padding: "4px 12px", background: "#f8f9fa" }}>
                        <pre style={{ margin: 0, fontSize: "0.75em", whiteSpace: "pre-wrap", color: "#555" }}>{item.raw_lines.join("\n")}</pre>
                      </td>
                    </tr>
                  ) : null}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          </div>

          <div className="table-footer mt-3">
            <Button variant="outline-primary" className="me-2">
              Export to CSV
            </Button>
            <Button variant="outline-secondary">Print</Button>
          </div>
        </div>
      )}

      {rawResponse && (
        <div className="table-section card p-4 mt-4">
          <h5 className="mb-3">Raw Python Response</h5>
          <pre className="bg-light p-3 rounded text-wrap">
            {JSON.stringify(rawResponse, null, 2)}
          </pre>
        </div>
      )}

      {!invoiceData && !error && (
        <Alert variant="info">
          <h6>How to use:</h6>
          <ul className="mb-0">
            <li>Download the template or use your own invoice file</li>
            <li>Supported formats:PDF</li>
            <li>For PDF: The parser will extract text and identify line items</li>
            <li>Upload the file and it will display all line items in a table</li>
          </ul>
        </Alert>
      )}
    </Container>
  );
}

export default InvoiceUpload;
